import {
	AG_HEADER_HEIGHT,
	AG_ROW_HEIGHT,
	AG_STATUS_BAR_TOTAL_FILTERED,
} from "@/utils/ag-grid-defaults";
import SearchIcon from "@material-ui/icons/Search";
import { useEffect, useState } from "react";

import { useRootTypeContext } from "@/pages/configurations/rootTypes/context/RootTypeContext";
import {
	Button,
	FormControl,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@material-ui/core";
import { Theme, createStyles, makeStyles } from "@material-ui/core/styles";
import { GridReadyEvent } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { CreateLocation } from "../CreateLocation/CreateLocation";
import { DeleteLocation } from "../DeleteLocation/DeleteLocation";
import { EditLocation } from "../EditLocation/EditLocation";
import { useTableData } from "./customHooks/useTableData";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiDataGrid-row": {
				cursor: "pointer",
			},
			"& .MuiDataGrid-columnHeaderTitle, & .MuiTablePagination-caption": {
				fontWeight: "bold",
				fontSize: "12px",
			},
			"& .MuiDataGrid-columnHeaderTitleContainer": {
				padding: "0",
			},
			"& .MuiDataGrid-cell--textLeft": {
				fontSize: "12px",
			},
		},
		relative: {
			position: "relative",
		},
		textfield: {
			margin: theme.spacing(2),
		},
		btnModal: {
			display: "flex",
			justifyContent: "flex-end",
		},
		dataTable: {
			padding: theme.spacing(1, 2),
		},
	}),
);

type Props = {
	isLoading: boolean;
	getLocationsFromAPI: Function;
};

export const TableLocations = ({ isLoading, getLocationsFromAPI }: Props) => {
	const [gridAPI, setGridAPI] = useState<any>(null);

	const [filterInput, setFilterInput] = useState("");
	const [rootTypeFilter, setRootTypeFilter] = useState("");

	const {
		state: { rootTypes },
	} = useRootTypeContext();

	const [modalCreateLocation, setModalCreateLocation] = useState(false);
	const [modalEditLocation, setModalEditLocation] = useState(false);
	const [modalDeleteLocation, setModalDeleteLocation] = useState(false);
	const [modalLocationType, setModalLocationType] = useState(false);

	const [locationIdForEdit, setLocationIdForEdit] = useState<number | null>(
		null,
	);
	const [locationIdForDelete, setLocationIdForDelete] = useState<number | null>(
		null,
	);

	const classes = useStyles();

	const { rows, columns, defaultColDef } = useTableData({
		setLocationIdForEdit,
		setModalEditLocation,
		setLocationIdForDelete,
		setModalDeleteLocation,
		setModalLocationType,
	});

	const handlerFilter = (e: any) => {
		setFilterInput(e.target.value);
	};

	const handleRootTypeChange = (e: any) => {
		setRootTypeFilter(e.target.value as string);
	};

	const onGridReady = (params: GridReadyEvent) => {
		setGridAPI(params.api);
		params.api.sizeColumnsToFit();
	};

	useEffect(() => {
		if (isLoading) {
			gridAPI?.showLoadingOverlay();
		} else {
			gridAPI?.hideOverlay();
		}
	}, [isLoading, gridAPI]);

	useEffect(() => {
		if (rootTypeFilter) {
			(async () => {
				gridAPI?.showLoadingOverlay();

				const rootTypeIdFilterInstance =
					gridAPI?.getFilterInstance("rootTypeId");

				rootTypeIdFilterInstance?.setModel({
					values: [rootTypeFilter],
				});

				gridAPI?.onFilterChanged();
				gridAPI?.hideOverlay();
			})();
		} else {
			const rootTypeIdFilterInstance = gridAPI?.getFilterInstance("rootTypeId");
			rootTypeIdFilterInstance?.setModel(null);
			gridAPI?.onFilterChanged();
		}
	}, [rootTypeFilter]);

	useEffect(() => {
		gridAPI?.setQuickFilter(filterInput);
	}, [filterInput, gridAPI]);

	return (
		<Grid container justifyContent="center">
			<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
				<Typography
					variant="h5"
					style={{ margin: "10px 0 6px", fontWeight: 600 }}
				>
					Locations Management
				</Typography>
			</Grid>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid
					item
					md={12}
					xs={12}
					style={{ padding: "0 15px" }}
					className={classes.relative}
				>
					<CreateLocation
						show={modalCreateLocation}
						onHide={(shouldUpdate: boolean) => {
							setModalCreateLocation(false);
							if (shouldUpdate) getLocationsFromAPI().then(() => {});
						}}
					/>
					<EditLocation
						locationIdForEdit={locationIdForEdit}
						show={modalEditLocation}
						onHide={(shouldUpdate: boolean) => {
							setModalEditLocation(false);
							if (shouldUpdate) getLocationsFromAPI().then(() => {});
						}}
					/>
					<DeleteLocation
						locationIdForDelete={locationIdForDelete}
						show={modalDeleteLocation}
						onHide={(shouldUpdate: boolean) => {
							setModalDeleteLocation(false);
							if (shouldUpdate) getLocationsFromAPI().then(() => {});
						}}
					/>

					<div style={{ display: "flex", gap: 16, padding: "10px 0" }}>
						<div style={{ flex: 1 }}>
							<FormControl
								style={{ width: "100%" }}
								variant="outlined"
								size="small"
								margin="dense"
							>
								<InputLabel id="root-type-select-label">Root Type</InputLabel>
								<Select
									style={{ width: "100%" }}
									value={rootTypeFilter}
									label="Root Type"
									onChange={handleRootTypeChange}
									disabled={isLoading}
								>
									<MenuItem value={""}>All</MenuItem>
									{!isLoading &&
										rootTypes?.map((rt) => {
											return (
												<MenuItem value={rt?.rootTypeName} key={rt?.rootTypeId}>
													{rt?.rootTypeName}
												</MenuItem>
											);
										})}
								</Select>
							</FormControl>
						</div>
						<div style={{ flex: 1 }}>
							<TextField
								fullWidth
								label="Search"
								variant="outlined"
								size="small"
								margin="dense"
								value={filterInput}
								onChange={handlerFilter}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
								}}
							/>
						</div>
					</div>

					<div
						className="ag-theme-balham"
						style={{ width: "100%", height: "60vh" }}
					>
						<AgGridReact
							onGridReady={onGridReady}
							rowData={rows}
							columnDefs={columns}
							defaultColDef={defaultColDef}
							rowHeight={AG_ROW_HEIGHT}
							headerHeight={AG_HEADER_HEIGHT}
							animateRows={true}
							rowSelection="single"
							statusBar={AG_STATUS_BAR_TOTAL_FILTERED}
						/>
					</div>
					<Grid container justifyContent="flex-end">
						<Grid item md={2} xs={12} className={classes.btnModal}>
							<Button
								variant="contained"
								color="primary"
								onClick={() => setModalCreateLocation(!modalCreateLocation)}
								fullWidth
								style={{ marginTop: 10 }}
							>
								New Location
							</Button>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
